import { Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useSuspenseLogsQuery } from '../shared/queries';
import dayjs from 'dayjs';
import { LogLine } from '../features/logs/components/LogLine';
import { getLogActionLabel, getLogCategoryLabel } from '../features/logs/category-label';

const Logs = () => {
  const { data: logs } = useSuspenseLogsQuery();
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [category, setCategory] = useState('');
  const [action, setAction] = useState('');

  const logsMatchingDates = useMemo(
    () =>
      logs.filter(
        log =>
          (!fromDate || !dayjs(log.createdAt).isBefore(dayjs(fromDate), 'day')) &&
          (!toDate || !dayjs(log.createdAt).isAfter(dayjs(toDate), 'day')),
      ),
    [fromDate, logs, toDate],
  );
  const categories = useMemo(
    () => [...new Set(logsMatchingDates.map(log => log.entityType).filter(Boolean))].sort(),
    [logsMatchingDates],
  );
  const actions = useMemo(
    () =>
      [
        ...new Set(
          logsMatchingDates
            .filter(log => !category || log.entityType === category)
            .map(log => log.action)
            .filter(Boolean),
        ),
      ].sort(),
    [category, logsMatchingDates],
  );

  useEffect(() => {
    if (action && !actions.includes(action)) setAction('');
  }, [action, actions]);
  const filteredLogs = useMemo(
    () =>
      logs.filter(log => {
        const logDate = dayjs(log.createdAt);
        return (
          (!category || log.entityType === category) &&
          (!action || log.action === action) &&
          (!fromDate || !logDate.isBefore(dayjs(fromDate), 'day')) &&
          (!toDate || !logDate.isAfter(dayjs(toDate), 'day'))
        );
      }),
    [action, category, fromDate, logs, toDate],
  );

  const resetFilters = () => {
    setFromDate('');
    setToDate('');
    setCategory('');
    setAction('');
  };

  const handleCategoryChange = (nextCategory: string) => {
    setCategory(nextCategory);
    setAction('');
  };

  return (
    <Stack rowGap={1.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ md: 'center' }}>
        <TextField
          label="Od data"
          type="date"
          size="small"
          value={fromDate}
          onChange={event => setFromDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Do data"
          type="date"
          size="small"
          value={toDate}
          onChange={event => setToDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          select
          label="Kategorie"
          size="small"
          value={category}
          onChange={event => handleCategoryChange(event.target.value)}
          sx={{ minWidth: 190 }}
        >
          <MenuItem value="">Všechny kategorie</MenuItem>
          {categories.map(item => (
            <MenuItem key={item} value={item}>
              {getLogCategoryLabel(item)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Akce"
          size="small"
          value={action}
          onChange={event => setAction(event.target.value)}
          sx={{ minWidth: 190 }}
        >
          <MenuItem value="">Všechny akce</MenuItem>
          {actions.map(item => (
            <MenuItem key={item} value={item}>
              {getLogActionLabel(item)}
            </MenuItem>
          ))}
        </TextField>
        <Button onClick={resetFilters} disabled={!fromDate && !toDate && !category && !action}>
          Zrušit filtry
        </Button>
      </Stack>

      <Typography variant="caption" color="text.secondary">
        Zobrazeno záznamů: {filteredLogs.length} z {logs.length}
      </Typography>

      {filteredLogs.map(log => (
        <LogLine
          key={log.id}
          dateTime={dayjs(log.createdAt)}
          description={log.message}
          userName={log.user.name}
          category={log.entityType}
          action={getLogActionLabel(log.action)}
        />
      ))}
      {filteredLogs.length === 0 && (
        <Typography color="text.secondary">Filtru neodpovídají žádné záznamy.</Typography>
      )}
    </Stack>
  );
};

export default Logs;
